import { BuildType } from 'components/changelog-page/ChangelogBase'
import Svg from 'components/UI/SVG'
import { graphql, useStaticQuery } from 'gatsby'
import CheckIcon from 'images/changelog-check.svg'
import CogIcon from 'images/cog-icon.svg'
import Separator from 'images/separator.svg'
import { AccordionArrow, AccordionWrapper } from 'pages/terms/oss'
import { ReactElement } from 'react'
import { Flex, Text } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import ExclamationIcon from 'images/exclamation-icon.svg'
import ChangelogDownloadIcon from 'images/changelog-download-icon.svg'
import ExternalLink from 'components/UI/ExternalLink'

export default function ChangelogAccordion({
  version,
  buildType,
  releaseDate,
  content,
  href,
  dataTestId,
}: {
  version: string
  buildType: BuildType
  releaseDate: string
  content: string
  href?: string
  dataTestId?: string
}): ReactElement {
  const accordionData = useStaticQuery<Queries.ChangelogAccordionQuery>(graphql`
    query ChangelogAccordion {
      directus {
        general_translations {
          strings {
            languages_code {
              code
            }
            guinea_pig
            beta
            released
            release
          }
        }
      }
    }
  `)

  const generalTranslationsStrings = getEnglishStrings(
    accordionData.directus?.general_translations?.strings,
  )

  return (
    <AccordionWrapper content={content} dataTestId={dataTestId}>
      <Flex
        sx={{
          gap: [0, '1.6rem'],
          alignItems: ['baseline', 'center'],
          flexWrap: 'wrap',
          width: ['min-content', 'max-content'],
        }}
      >
        <Text variant="heading2" sx={{ minWidth: '10rem', color: 'white' }} className="text">
          v{version}
        </Text>
        <Svg svg={Separator} sx={{ height: '2.4rem', display: ['none', 'block'] }} />
        <Flex sx={{ alignItems: 'center', gap: ['0.4rem', '0.8rem'] }}>
          <Svg
            svg={
              {
                guinea_pig: ExclamationIcon,
                beta: CogIcon,
                release: CheckIcon,
              }[buildType]
            }
            sx={{
              width: ['1.6rem', '2.4rem'],
              height: ['1.6rem', '2.4rem'],
              path: {
                fill: 'white',
                opacity: 1,
                fillOpacity: 1,
              },
              g: {
                opacity: 1,
              },
            }}
            className="svg"
          />
          <Flex
            sx={{
              flexDirection: ['row', 'column'],
              alignItems: ['center', 'flex-start'],
              gap: ['0.8rem', 0],
              width: ['min-content', 'max-content'],
            }}
          >
            <Text
              variant="paragraphSmallBold"
              sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
              className="text"
            >
              {generalTranslationsStrings?.[buildType]}
            </Text>
            <Text
              variant="paragraphSmallest"
              sx={{ color: 'white50', whiteSpace: 'nowrap' }}
              className="text"
            >
              <Text sx={{ display: ['none', 'inline-flex'] }}>
                {generalTranslationsStrings?.released}&nbsp;
              </Text>
              {releaseDate}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex
        sx={{
          alignItems: 'center',
          gap: '1.6rem',
        }}
      >
        <ExternalLink
          href={href}
          sxx={{ ':hover': { '& g': { opacity: 1 } } }}
          onClick={e => e.stopPropagation()}
          data-testid="download-link"
        >
          <Svg svg={ChangelogDownloadIcon} sx={{ minHeight: '2.4rem', minWidth: '2.4rem' }} />
        </ExternalLink>
        <Svg svg={Separator} sx={{ height: '2.4rem', minWidth: '0.2rem' }} />
        <AccordionArrow />
      </Flex>
    </AccordionWrapper>
  )
}
